import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import API from '../../utils/api';
import { FaHistory } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

const ACTION_ICONS = {
  LOGIN: '🔐',
  LOGOUT: '🚪',
  MEMBER_REGISTERED: '📝',
  ADMIN_CREATED: '👤',
  MEMBER_DELETED: '🗑️',
  STATUS_CHANGED: '✏️',
  MEMBER_UPDATED: '✏️',
  ADMIN_DELETED: '🗑️'
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  let s = String(dateStr).trim();
  if (!s.includes('T')) {
    s = s.replace(' ', 'T');
  }
  // If no timezone offset or Z, append Z so it is correctly parsed as UTC
  if (!s.endsWith('Z') && !/[+-]\d{2}(:?\d{2})?$/.test(s)) {
    s += 'Z';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(dateStr);

  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

const getAdminRole = (log) => {
  const role = (log.admin_role || '').trim().toLowerCase();
  if (role === 'superadmin' || role === 'super admin' || (log.details && /super admin/i.test(log.details))) {
    return 'Super Admin';
  }
  if (role === 'admin') {
    return 'Admin';
  }
  if (log.admin_role) {
    return log.admin_role.charAt(0).toUpperCase() + log.admin_role.slice(1);
  }
  return 'Admin';
};

export default function ActivityLogs() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchLogs();
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await API.get('/admins/logs/activity');
      const adminOnly = (res.data || []).filter(l => l.user_type === 'admin');
      setLogs(adminOnly);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', fontFamily: 'Poppins, sans-serif' }}>Activity Logs</h1>
          <p style={{ fontSize: 13, color: '#6b7280' }}>Recent administrator actions and events</p>
        </div>
      </div>
      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaHistory color="#16a34a" />
              <h3 style={{ fontWeight: 700, fontSize: 16 }}>Admin Activity ({logs.length} records)</h3>
            </div>
          </div>
          <div className="table-container">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner"></div></div>
            ) : logs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h3>No activity yet</h3>
                <p>System actions will appear here</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th><th>Action</th><th>Details</th><th>User Type</th><th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const roleName = getAdminRole(log);
                    const isSuper = roleName === 'Super Admin';
                    return (
                      <tr key={log.id}>
                        <td style={{ color: '#9ca3af', fontSize: 12 }}>{i + 1}</td>
                        <td>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span>{ACTION_ICONS[log.action] || '📌'}</span>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{log.action.replace(/_/g, ' ')}</span>
                          </span>
                        </td>
                        <td style={{ color: '#4b5563', fontSize: 13 }}>{log.details}</td>
                        <td>
                          <span className={`badge ${isSuper ? 'badge-success' : 'badge-blue'}`}>
                            {roleName}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: '#374151', whiteSpace: 'nowrap' }}>
                          {formatDateTime(log.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
